import {
  Box,
  Divider,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ForwardedRef,
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
} from 'react';
import {
  Control,
  useFieldArray,
  useForm,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormWatch,
  Controller,
  UseFormSetValue,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import DeleteFileRedIcon from '../../../../components/Icons/DeleteFileRedIcon';
import CustomButton from '../../../../components/MUIComponents/CustomButton';
import Input from '../../../../components/MUIComponents/Input';
import DateInput from '../../../../components/MUIComponents/DateInput';
import { ButtonTypeEnum } from '../../../../enums/button-type-enum';
import { OTHER_SPORT } from '../../../../constants';
import { useGetSportsQuery } from '../../../../store/apiSlice/sportApi';
import SportList from '../../../../components/Auth/VerifyAccount/SportVerification/SportList/SportList';
import UploadIcon from '../../../../components/Icons/UploadIcon';
import SportBackgroundImageFileItem from '../../../../components/Auth/VerifyAccount/SportVerification/SportBackgroundImageFileItem/SportBackgroundImageFileItem';
import { ElementIdEnum } from '../../../../enums/element-id-enum';
import {
  isSupportedSportBackgroundFileHelper,
  validateFileSize,
} from '../../../../utils/helper/file-validation-helper';
import TeamRow from './components/TeamRow';
import {
  ISportBackground,
  ITeam,
  ISportBackgroundFileData,
} from '../../../../interfaces/user-sport-background.interface';
import { createSportBackgroundSchema } from '../../../../validation/sport-validation';

export interface EditSportBackgroundFormInputs {
  sportBackgrounds: ISportBackground[];
}

const editSportBackgroundFormSchema: yup.AnyObjectSchema = yup.object({
  sportBackgrounds: yup
    .array()
    .of(
      yup.lazy((sb: any) => {
        const isOtherSport = !!sb?.isOtherSport;

        return createSportBackgroundSchema(isOtherSport).concat(
          yup.object({
            sportId: yup.string().required('Sport is required'),
          })
        );
      })
    )
    .min(1, 'At least one sport is required')
    .required('Sport backgrounds are required'),
});

export interface EditFormRef {
  control: Control<EditSportBackgroundFormInputs, any>;
  reset: UseFormReset<EditSportBackgroundFormInputs>;
  watch: UseFormWatch<EditSportBackgroundFormInputs>;
  handleSubmit: UseFormHandleSubmit<EditSportBackgroundFormInputs>;
  scrollToError: () => void;
}

interface EditSportBackgroundFormProps {
  savedSportBackgrounds: ISportBackground[];
  onCancel?: () => void;
}

const DEFAULT_TEAM: ITeam = {
  isIndividualSport: false,
  isActiveTeam: false,
  leagueId: '',
  leagueName: '',
  teamId: '',
  teamName: '',
  startDateInTeam: undefined as any,
  endDateInTeam: undefined as any,
};

const EditSportBackgroundForm = forwardRef<
  EditFormRef,
  EditSportBackgroundFormProps
>(({ savedSportBackgrounds }, ref: ForwardedRef<EditFormRef>) => {
  const theme = useTheme();
  const _mobileView = useMediaQuery(theme.breakpoints.down('md'));

  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
  } = useForm<EditSportBackgroundFormInputs>({
    resolver: yupResolver(editSportBackgroundFormSchema, {
      abortEarly: false,
    }),
    defaultValues: {
      sportBackgrounds: savedSportBackgrounds,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { data: sports = [] } = useGetSportsQuery();

  const watchedSportBackgrounds = watch('sportBackgrounds');

  const {
    fields: sportFields,
    append: appendSport,
    remove: removeSport,
  } = useFieldArray({ control, name: 'sportBackgrounds' });

  useImperativeHandle(ref, () => ({
    control,
    reset,
    watch,
    handleSubmit,
    scrollToError,
  }));

  const scrollToError = () => {
    // Find the first error field and scroll to it
    const findFirstErrorPath = (obj: any, path = ''): string | null => {
      if (!obj || typeof obj !== 'object') return null;

      // Check if this is a field error (has message property)
      if (obj.message) return path;

      // Traverse nested objects/arrays
      for (const key in obj) {
        const newPath = path ? `${path}.${key}` : key;
        const result = findFirstErrorPath(obj[key], newPath);
        if (result) return result;
      }
      return null;
    };

    const errorPath = findFirstErrorPath(errors);
    if (!errorPath) return;

    // Extract sportIndex and teamIndex from error path
    // e.g., "sportBackgrounds.0.teams.1.leagueName" -> sportIndex=0, teamIndex=1
    const pathParts = errorPath.split('.');

    // Check if error is in teams section
    const teamsIndex = pathParts.indexOf('teams');
    if (teamsIndex !== -1 && pathParts[teamsIndex + 1]) {
      const sportIndex = pathParts[1]; // sportBackgrounds.0
      const teamIndex = pathParts[teamsIndex + 1]; // teams.1

      // Scroll to the team row container
      const teamRowElement = document.getElementById(
        `team-row-${sportIndex}-${teamIndex}`
      );

      if (teamRowElement) {
        teamRowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Try to focus the first input in the team row after scrolling
        setTimeout(() => {
          const firstInput = teamRowElement.querySelector('input');
          if (firstInput) {
            firstInput.focus();
          }
        }, 300);
        return;
      }
    }

    // Fallback: scroll to first input with error by ID
    const sportIndex = pathParts[1];
    const fieldName = pathParts[pathParts.length - 1];
    const elementId = `sportBackground-${sportIndex}-${fieldName}`;
    const element = document.getElementById(elementId);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => element.focus(), 100);
    }
  };

  useEffect(() => {
    if (sports.length === 0) return;

    watchedSportBackgrounds?.forEach((sb, sportIndex) => {
      const selectedSportId = sb?.sportId;
      if (!selectedSportId) return;
      if (selectedSportId === OTHER_SPORT.id) return;

      const isManualSport =
        sports.findIndex((s: any) => s.id === selectedSportId) === -1;

      if (isManualSport && !sb?.isOtherSport) {
        setValue(`sportBackgrounds.${sportIndex}.isOtherSport` as const, true);
      }
    });
  }, [sports, watchedSportBackgrounds, setValue]);

  return (
    <>
      {sportFields.map((item, sportIndex) => {
        const isActiveSport = watch(
          `sportBackgrounds.${sportIndex}.isActiveSport` as const
        );
        const selectedSportId = watch(
          `sportBackgrounds.${sportIndex}.sportId` as const
        );
        const isOtherSport = watch(
          `sportBackgrounds.${sportIndex}.isOtherSport` as const
        );

        const isManualSport =
          !!selectedSportId &&
          selectedSportId !== OTHER_SPORT.id &&
          sports.length > 0 &&
          sports.findIndex((s: any) => s.id === selectedSportId) === -1;

        const effectiveIsOtherSport = !!isOtherSport || isManualSport;

        const handleSportToggle = (sportId: string) => {
          const isOther = sportId === OTHER_SPORT.id;

          if (isOther) {
            setValue(
              `sportBackgrounds.${sportIndex}.isOtherSport` as const,
              true
            );
            return;
          }

          setValue(`sportBackgrounds.${sportIndex}.sportId` as const, sportId);
          setValue(
            `sportBackgrounds.${sportIndex}.isOtherSport` as const,
            false
          );
          setValue(
            `sportBackgrounds.${sportIndex}.isIndividualSport` as const,
            false
          );
        };

        return (
          <Fragment key={item.id}>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={2}
            >
              {sportFields.length > 1 && (
                <IconButton onClick={() => removeSport(sportIndex)}>
                  <DeleteFileRedIcon />
                </IconButton>
              )}
            </Box>

            <Grid
              container
              sx={{ rowGap: '28px', '& > .MuiGrid-item': { width: 1 } }}
            >
              {/* Sport Selection */}
              <Grid item>
                <Box display="flex" flexDirection="column" gap="16px">
                  <Typography variant="h3">What did you play?</Typography>
                  <SportList
                    sports={sports}
                    handleSportToggle={handleSportToggle}
                    selectedSportId={
                      effectiveIsOtherSport
                        ? OTHER_SPORT.id
                        : selectedSportId || null
                    }
                    isEditSportBg
                  />
                  {errors?.sportBackgrounds?.[sportIndex]?.sportId && (
                    <Typography variant="caption" color="error.main">
                      {errors?.sportBackgrounds?.[sportIndex]?.sportId?.message}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Other Sport Name Input */}
              {effectiveIsOtherSport && (
                <Grid item>
                  <Box
                    mt="24px"
                    display="flex"
                    flexDirection="column"
                    gap="16px"
                  >
                    <Input
                      caption="Sport name"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter sport name"
                      register={register(
                        `sportBackgrounds.${sportIndex}.sportName` as const
                      )}
                      error={
                        !!errors?.sportBackgrounds?.[sportIndex]?.sportName
                      }
                      helperText={
                        errors?.sportBackgrounds?.[sportIndex]?.sportName
                          ?.message
                      }
                    />
                  </Box>
                </Grid>
              )}

              {/* Individual Sport Checkbox */}
              {effectiveIsOtherSport && (
                <Grid item>
                  <Controller
                    name={
                      `sportBackgrounds.${sportIndex}.isIndividualSport` as const
                    }
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            sx={{ padding: 0, margin: 0 }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 400, fontSize: '14px' }}
                          >
                            Individual sport
                          </Typography>
                        }
                        sx={{
                          padding: 0,
                          margin: 0,
                          gap: '8px',
                          width: '100%',
                          alignItems: 'center',
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Career Timeline */}
              <Grid item>
                <Box display="flex" flexDirection="column">
                  <Typography variant="body4" fontWeight={700} padding="0 2px">
                    Your career timeline:
                  </Typography>
                  <Grid
                    container
                    sx={{
                      display: 'flex',
                      gap: '1.375rem',
                      mt: '12px',
                    }}
                  >
                    <Grid item width={184}>
                      <DateInput
                        control={control}
                        name={
                          `sportBackgrounds.${sportIndex}.startCareerDate` as const
                        }
                        placeholder="Your start date"
                        error={
                          errors?.sportBackgrounds?.[sportIndex]
                            ?.startCareerDate
                        }
                        helperText={
                          errors?.sportBackgrounds?.[sportIndex]
                            ?.startCareerDate?.message
                        }
                      />
                    </Grid>
                    <Grid item width={184}>
                      {!isActiveSport && (
                        <DateInput
                          control={control}
                          name={
                            `sportBackgrounds.${sportIndex}.endCareerDate` as const
                          }
                          placeholder="Your end date"
                          error={
                            errors?.sportBackgrounds?.[sportIndex]
                              ?.endCareerDate
                          }
                          helperText={
                            errors?.sportBackgrounds?.[sportIndex]
                              ?.endCareerDate?.message
                          }
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Active Sport Checkbox */}
              <Grid item>
                <Box>
                  <Controller
                    name={
                      `sportBackgrounds.${sportIndex}.isActiveSport` as const
                    }
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!field.value}
                            onChange={(_, checked) => field.onChange(checked)}
                            sx={{ padding: 0, margin: 0 }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 400, fontSize: '14px' }}
                          >
                            My career in athletics is ongoing
                          </Typography>
                        }
                        sx={{
                          padding: 0,
                          margin: 0,
                          gap: '8px',
                          width: '100%',
                          alignItems: 'center',
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Description */}
              <Grid item>
                <Box
                  display="flex"
                  gridColumn="span 2"
                  rowGap="36px"
                  columnGap="16px"
                >
                  <Input
                    caption="Describe your sport background:"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Few words about your sports experience"
                    register={register(
                      `sportBackgrounds.${sportIndex}.description` as const
                    )}
                    error={
                      !!errors?.sportBackgrounds?.[sportIndex]?.description
                    }
                    helperText={
                      errors?.sportBackgrounds?.[sportIndex]?.description
                        ?.message
                    }
                    containerFlex="1"
                  />
                </Box>
              </Grid>

              {/* Teams Section */}
              <Grid item sx={{ mt: '12px' }}>
                <Typography variant="h3" sx={{ mb: '12px' }}>
                  {watch(
                    `sportBackgrounds.${sportIndex}.isIndividualSport` as const
                  )
                    ? 'Leagues'
                    : 'Leagues & Teams'}
                </Typography>
                <TeamsSection
                  control={control}
                  sportIndex={sportIndex}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  register={register}
                  trigger={trigger}
                />
              </Grid>

              {/* Sport Experience */}
              <Grid item>
                <Box display="flex" flexDirection="column" gap="12px">
                  <Typography variant="h3">
                    Confirm Your Sport Experience
                  </Typography>
                  <Typography variant="body3">
                    Upload photos, videos or documents of your awards,
                    certificates, etc.
                  </Typography>
                </Box>
                <Box mt="36px">
                  <SportBackgroundFileUpload
                    control={control}
                    sportIndex={sportIndex}
                    watch={watch}
                    setValue={setValue}
                  />
                </Box>
              </Grid>
            </Grid>

            {sportIndex < sportFields.length - 1 && <Divider sx={{ my: 4 }} />}
          </Fragment>
        );
      })}

      {/* Add Another Sport Button */}
      <CustomButton
        buttonWidth="100%"
        variantType={ButtonTypeEnum.SECONDARY}
        sx={{ borderColor: 'background.dark', mt: '36px', height: 58 }}
        onClick={() =>
          appendSport({
            sportId: '',
            isOtherSport: false,
            isIndividualSport: false,
            isActiveSport: false,
            startCareerDate: undefined as any,
            endCareerDate: undefined as any,
            description: '',
            teams: [DEFAULT_TEAM],
            fileData: [],
          })
        }
        type="button"
      >
        ADD SPORT BACKGROUND
      </CustomButton>
    </>
  );
});

EditSportBackgroundForm.displayName = 'EditSportBackgroundForm';

export default EditSportBackgroundForm;

// File Upload Component
interface SportBackgroundFileUploadProps {
  control: Control<EditSportBackgroundFormInputs, any>;
  sportIndex: number;
  watch: UseFormWatch<EditSportBackgroundFormInputs>;
  setValue: UseFormSetValue<EditSportBackgroundFormInputs>;
}

const SportBackgroundFileUpload = ({
  sportIndex,
  watch,
  setValue,
}: SportBackgroundFileUploadProps) => {
  const fileData =
    watch(`sportBackgrounds.${sportIndex}.fileData` as const) || [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    files.forEach((file) => {
      if (!isSupportedSportBackgroundFileHelper(file)) {
        alert(`${file.name} is not a supported file type`);
        return;
      }

      const validation = validateFileSize(file);
      if (!validation.valid) {
        alert(validation.error || `${file.name} is too large`);
        return;
      }

      const newFile: ISportBackgroundFileData = {
        file,
      };

      setValue(`sportBackgrounds.${sportIndex}.fileData` as const, [
        ...fileData,
        newFile,
      ]);
    });
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = fileData.filter((_: any, i: number) => i !== index);
    setValue(`sportBackgrounds.${sportIndex}.fileData` as const, updatedFiles);
  };

  return (
    <Box display="flex" flexDirection="column" gap="36px">
      {fileData.length > 0 && (
        <Box display="flex" flexDirection="column" gap="22px">
          {fileData.map((file: ISportBackgroundFileData, index: number) => (
            <Box
              key={index}
              width="100%"
              padding="22px"
              border="1px solid"
              borderColor="background.darker"
              borderRadius="1px"
            >
              <SportBackgroundImageFileItem
                fileData={file}
                index={index}
                removeFileFromBackground={() => handleRemoveFile(index)}
                handleFileDataChange={(idx, data) => {
                  const updatedFiles = [...fileData];
                  updatedFiles[idx] = { ...updatedFiles[idx], ...data };
                  setValue(
                    `sportBackgrounds.${sportIndex}.fileData` as const,
                    updatedFiles
                  );
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      <Box>
        <input
          type="file"
          id={`${ElementIdEnum.SPORT_BACKGROUND_FILE_INPUT}-${sportIndex}`}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept="image/png,image/jpeg,image/webp,application/pdf"
          multiple
        />
        <Box
          onClick={() => {
            const input = document.getElementById(
              `${ElementIdEnum.SPORT_BACKGROUND_FILE_INPUT}-${sportIndex}`
            ) as HTMLInputElement;
            input?.click();
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '60px 0',
            bgcolor: 'background.light',
            border: '1px dashed',
            borderColor: 'background.dark',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Box display="flex" flexDirection="column" gap="36px">
            <Box display="flex" width="100%" justifyContent="center">
              <UploadIcon />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
              maxWidth="530px"
              margin="0 auto"
              gap="22px"
            >
              <Typography variant="body4" textAlign="center">
                Drag & Drop files here or{' '}
                <Box
                  component="span"
                  sx={{ textDecoration: 'underline', color: 'text.accent' }}
                >
                  choose files
                </Box>{' '}
                to upload
              </Typography>
              <Typography
                variant="body4"
                color="text.secondary"
                fontWeight={500}
              >
                PNG, JPG, WebP or PDF (max 100MB for images, 1GB for PDF)
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Teams Section Component
interface TeamsSectionProps {
  control: Control<EditSportBackgroundFormInputs, any>;
  sportIndex: number;
  watch: UseFormWatch<EditSportBackgroundFormInputs>;
  setValue: UseFormSetValue<EditSportBackgroundFormInputs>;
  errors: any;
  register: any;
  trigger: any;
}

const TeamsSection = ({
  control,
  sportIndex,
  watch,
  setValue,
  errors,
  register,
  trigger,
}: TeamsSectionProps) => {
  const {
    fields: teamFields,
    append: appendTeam,
    remove: removeTeam,
  } = useFieldArray({
    control,
    name: `sportBackgrounds.${sportIndex}.teams` as const,
  });

  const isIndividualSport = watch(
    `sportBackgrounds.${sportIndex}.isIndividualSport` as const
  );

  return (
    <>
      {/* <Grid item>
        <Typography variant="body4" fontWeight={700} padding="0 2px" mb="12px">
          Teams & Leagues:
        </Typography>
        <Divider sx={{ mt: '12px', mb: '12px' }} />
      </Grid> */}

      {teamFields.map((teamItem, teamIndex) => (
        <TeamRow
          key={teamItem.id}
          control={control}
          sportIndex={sportIndex}
          teamIndex={teamIndex}
          totalTeams={teamFields.length}
          watch={watch}
          setValue={setValue}
          errors={errors}
          register={register}
          trigger={trigger}
          onRemove={() => removeTeam(teamIndex)}
          canRemove={teamFields.length > 1}
          isIndividualSport={isIndividualSport}
        />
      ))}

      <CustomButton
        buttonWidth="100%"
        variantType={ButtonTypeEnum.SECONDARY}
        sx={{ borderColor: 'background.dark', mt: '16px', height: 50 }}
        onClick={() => appendTeam(DEFAULT_TEAM)}
        type="button"
      >
        {isIndividualSport ? 'ADD LEAGUE' : 'ADD TEAM'}
      </CustomButton>
    </>
  );
};
